/**
 * Design Remix — Instant Variations Engine
 *
 * Generates 3–5 alternative versions of the current design by mutating
 * visual parameters (color scheme, line art overlay, seed, grain, font pairing)
 * while keeping all text content, positions, and sizes identical.
 *
 * The algorithm avoids random soup by using curated pairings:
 *   - Schemes are grouped by temperature family (warm, cool, dark, light, vibrant)
 *   - Overlays have affinity scores with scheme families
 *   - Font pairings are hand-curated for aesthetic coherence
 *
 * Thumbnails are rendered via lightweight offscreen canvases using the existing
 * renderBackground() pipeline — no Fabric.js cloning required.
 *
 * @module remix
 */

import { SCHEMES, getScheme, listSchemes } from '../canvas/schemes.js';
import { LINE_ART_STYLES } from '../overlays/line-art.js';
import { renderBackground } from './background.js';
import { normalizeOverlay } from '../utils/normalize.js';

// ─────────────────────────────────────────────────────────────────────────────
// SCHEME FAMILY GROUPINGS
// ─────────────────────────────────────────────────────────────────────────────
// Each scheme is assigned to a temperature family. The remix algorithm biases
// 3 of 5 variations toward the same family as the current scheme (for coherence)
// and lets the other 2 pick freely (for surprise).

const SCHEME_FAMILIES = {
  warm:    ['warm', 'rust', 'copper', 'terra', 'ember', 'honey', 'sand', 'bone'],
  cool:    ['midnight', 'ocean', 'ice', 'ink', 'arctic', 'frost', 'steel', 'slate'],
  dark:    ['dark', 'smoke', 'charcoal', 'void', 'obsidian', 'noir', 'ash'],
  light:   ['light', 'cream', 'blush', 'pearl', 'lavender', 'sage', 'mint'],
  vibrant: ['red', 'neon', 'solar', 'sunset', 'cherry', 'coral', 'rose'],
  earthy:  ['forest', 'moss', 'wine', 'plum', 'dusk'],
};

// Reverse lookup: scheme key → family name
const SCHEME_TO_FAMILY = {};
for (const [family, keys] of Object.entries(SCHEME_FAMILIES)) {
  for (const k of keys) SCHEME_TO_FAMILY[k] = family;
}

// ─────────────────────────────────────────────────────────────────────────────
// OVERLAY ↔ SCHEME AFFINITY
// ─────────────────────────────────────────────────────────────────────────────
// Curated map of which overlays complement which scheme families.
// Each overlay lists its preferred families in order. The algorithm
// preferentially picks overlays that match the selected scheme's family.

const OVERLAY_AFFINITY = {
  flow:          ['dark', 'cool', 'earthy'],
  grid:          ['dark', 'vibrant', 'cool'],
  circles:       ['cool', 'dark', 'light'],
  topo:          ['earthy', 'warm', 'cool'],
  spectrograph:  ['vibrant', 'dark', 'cool'],
  waveform:      ['cool', 'dark', 'vibrant'],
  fire:          ['warm', 'vibrant', 'dark'],
  oscillator:    ['vibrant', 'dark', 'cool'],
  smiley:        ['light', 'vibrant', 'warm'],
  constellation: ['dark', 'cool', 'earthy'],
  circuit:       ['dark', 'cool', 'vibrant'],
  chevron:       ['warm', 'earthy', 'light'],
  diamonds:      ['light', 'warm', 'cool'],
  crosshatch:    ['earthy', 'warm', 'dark'],
  dots:          ['light', 'vibrant', 'cool'],
  spiral:        ['dark', 'vibrant', 'cool'],
  maze:          ['dark', 'cool', 'earthy'],
  lightning:     ['dark', 'vibrant', 'cool'],
  rain:          ['cool', 'dark', 'earthy'],
  hexgrid:       ['cool', 'dark', 'vibrant'],
  scatter:       ['light', 'vibrant', 'warm'],
  waves:         ['cool', 'earthy', 'dark'],
  bubbles:       ['light', 'cool', 'vibrant'],
  branches:      ['earthy', 'warm', 'dark'],
  ribbon:        ['vibrant', 'dark', 'cool'],
  static:        ['dark', 'vibrant', 'cool'],
  dna:           ['cool', 'dark', 'vibrant'],
  radar:         ['cool', 'dark', 'vibrant'],
  pulse:         ['vibrant', 'dark', 'cool'],
  shatter:       ['dark', 'vibrant', 'cool'],
  weave:         ['warm', 'earthy', 'light'],
  feather:       ['light', 'warm', 'earthy'],
  ripple:        ['cool', 'light', 'earthy'],
  glitch:        ['dark', 'vibrant', 'cool'],
  meteor:        ['dark', 'vibrant', 'warm'],
  terrain:       ['earthy', 'warm', 'cool'],
  lattice:       ['dark', 'cool', 'light'],
  crescents:     ['cool', 'dark', 'earthy'],
};

// ─────────────────────────────────────────────────────────────────────────────
// CURATED FONT PAIRINGS
// ─────────────────────────────────────────────────────────────────────────────
// Each pairing: [title font, body font].
// All fonts are from the bundled Google Fonts set — no network requests.

export const FONT_PAIRINGS = [
  { id: 'default',     title: 'Work Sans',        body: 'DM Mono',           label: 'Work Sans + DM Mono' },
  { id: 'impact',      title: 'Bebas Neue',       body: 'Inter',             label: 'Bebas Neue + Inter' },
  { id: 'techy',       title: 'Space Grotesk',    body: 'DM Mono',           label: 'Space Grotesk + DM Mono' },
  { id: 'condensed',   title: 'Oswald',           body: 'Inter',             label: 'Oswald + Inter' },
  { id: 'poster',      title: 'Bebas Neue',       body: 'Work Sans',         label: 'Bebas Neue + Work Sans' },
  { id: 'geometric',   title: 'Outfit',           body: 'DM Mono',           label: 'Outfit + DM Mono' },
  { id: 'geo-clean',   title: 'Space Grotesk',    body: 'Work Sans',         label: 'Space Grotesk + Work Sans' },
  { id: 'modern',      title: 'Montserrat',       body: 'Inter',             label: 'Montserrat + Inter' },
  { id: 'display',     title: 'Archivo Black',    body: 'Work Sans',         label: 'Archivo Black + Work Sans' },
  { id: 'retro',       title: 'Righteous',        body: 'DM Mono',           label: 'Righteous + DM Mono' },
  { id: 'tight',       title: 'Barlow Condensed', body: 'Inter',             label: 'Barlow Condensed + Inter' },
  { id: 'expressive',  title: 'Anton',            body: 'Work Sans',         label: 'Anton + Work Sans' },
];

// ─────────────────────────────────────────────────────────────────────────────
// VARIATION GENERATOR
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Simple seeded PRNG for deterministic variation generation.
 * Returns a function that produces values in [0, 1).
 */
function seededRng(seed) {
  let s = seed | 0;
  return () => {
    s ^= s << 13;
    s ^= s >> 17;
    s ^= s << 5;
    return ((s >>> 0) % 10000) / 10000;
  };
}

/**
 * Pick a random item from an array using the given RNG.
 */
function pick(arr, rng) {
  return arr[Math.floor(rng() * arr.length)];
}

/**
 * Jitter a numeric value by ±amount, clamped to [min, max].
 */
function jitter(value, amount, min, max, rng) {
  const delta = (rng() - 0.5) * 2 * amount;
  return Math.round(Math.max(min, Math.min(max, value + delta)));
}

/**
 * Get all scheme keys except the given one.
 */
function otherSchemes(currentKey) {
  return Object.keys(SCHEMES).filter(k => k !== currentKey);
}

/**
 * Get scheme keys from a specific family, excluding the current one.
 */
function familySchemes(family, currentKey) {
  const pool = SCHEME_FAMILIES[family] || [];
  return pool.filter(k => k !== currentKey && SCHEMES[k]);
}

/**
 * Pick an overlay that has affinity with the given scheme family.
 * Falls back to any non-none overlay.
 */
function pickAffinityOverlay(schemeFamily, currentOverlay, rng) {
  // Get all overlays that list this family in their affinity
  const compatible = Object.entries(OVERLAY_AFFINITY)
    .filter(([id, families]) => families.includes(schemeFamily) && id !== currentOverlay)
    .map(([id]) => id);

  if (compatible.length > 0 && rng() < 0.7) {
    return pick(compatible, rng);
  }
  // Fallback: any overlay except current and 'none'
  const all = LINE_ART_STYLES.filter(s => s.id !== 'none' && s.id !== currentOverlay).map(s => s.id);
  return pick(all, rng);
}

/**
 * Generate N variation configurations from the current design state.
 *
 * Each variation is a config object:
 *   { scheme, overlay, seed, opacity, grainDensity, grainOpacity, fontPairing, label }
 *
 * @param {object} currentState - current design state from app.js
 * @param {number} count - number of variations to generate (default 5)
 * @param {number} masterSeed - seed for the variation generator (for reproducibility)
 * @param {Set} seenSet - optional set of "scheme|overlay" strings to avoid duplicates
 * @returns {Array<object>} variation configs
 */
export function generateVariations(currentState, count = 5, masterSeed = null, seenSet = null) {
  const seed = masterSeed ?? Math.floor(Math.random() * 1000000);
  const rng = seededRng(seed);
  const seen = seenSet || new Set();

  // Current state extraction — normalize values that may still be strings
  // ("low"/"medium"/"high") or floats (0-1) from the template JSON
  const currentSchemeKey = currentState.scheme?.name || 'dark';
  const currentFamily = SCHEME_TO_FAMILY[currentSchemeKey] || 'dark';
  const currentOverlay = currentState.overlays?.lineArt?.type || 'flow';
  const currentDensity = normalizeOverlay(currentState.overlays?.lineArt?.density, 50);
  const currentOpacity = normalizeOverlay(currentState.overlays?.lineArt?.opacity, 30);
  const currentGrainDensity = normalizeOverlay(currentState.overlays?.grain?.density, 40);
  const currentGrainOpacity = normalizeOverlay(currentState.overlays?.grain?.opacity, 15);

  const variations = [];
  let attempts = 0;
  const maxAttempts = count * 10;

  while (variations.length < count && attempts < maxAttempts) {
    attempts++;

    // ── Scheme selection ──
    // 60% of variations stay in the same temperature family, 40% go wild
    let schemeKey;
    if (rng() < 0.6) {
      const sameFamily = familySchemes(currentFamily, currentSchemeKey);
      schemeKey = sameFamily.length > 0 ? pick(sameFamily, rng) : pick(otherSchemes(currentSchemeKey), rng);
    } else {
      schemeKey = pick(otherSchemes(currentSchemeKey), rng);
    }

    const schemeFamily = SCHEME_TO_FAMILY[schemeKey] || 'dark';

    // ── Overlay selection ──
    // Always pick a different overlay for visual variety. Biased toward
    // overlays that have affinity with the selected scheme's family.
    const overlay = pickAffinityOverlay(schemeFamily, currentOverlay, rng);

    // Deduplicate
    const sig = `${schemeKey}|${overlay}`;
    if (seen.has(sig)) continue;
    seen.add(sig);

    // ── Seed — always change for visual variety ──
    const artSeed = Math.floor(rng() * 100000);

    // ── Density/opacity — vary widely so thumbnails look distinct ──
    const density = jitter(currentDensity, 25, 20, 80, rng);
    const opacity = jitter(currentOpacity, 20, 15, 70, rng);
    const grainDensity = jitter(currentGrainDensity, 15, 15, 70, rng);
    const grainOpacity = jitter(currentGrainOpacity, 8, 5, 30, rng);

    // ── Font pairing (50% chance of change) ──
    let fontPairing = null;
    if (rng() < 0.5) {
      fontPairing = pick(FONT_PAIRINGS, rng);
    }

    // ── Build the variation config ──
    const scheme = getScheme(schemeKey);
    const overlayLabel = LINE_ART_STYLES.find(s => s.id === overlay)?.label || overlay;

    variations.push({
      schemeKey,
      schemeName: scheme.name || schemeKey,
      overlayType: overlay,
      overlayLabel,
      seed: artSeed,
      opacity,
      density,
      grainDensity,
      grainOpacity,
      fontPairing,
      label: `${scheme.name || schemeKey} + ${overlayLabel}`,
      _masterSeed: seed,
    });
  }

  return variations;
}

// ─────────────────────────────────────────────────────────────────────────────
// THUMBNAIL RENDERER
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Render a thumbnail preview of a variation config.
 * Uses the lightweight renderBackground() pipeline — draws scheme bg + line art + grain
 * at thumbnail resolution, then overlays mock text to give a sense of the layout.
 *
 * @param {object} variation - variation config from generateVariations()
 * @param {object} currentState - current design state (for text content / template info)
 * @param {object} currentSize - current TEMPLATE_SIZES entry
 * @param {number} thumbWidth - thumbnail pixel width (default 320)
 * @returns {string} data URL of the thumbnail
 */
export function renderVariationThumbnail(variation, currentState, currentSize, thumbWidth = 320) {
  const scheme = getScheme(variation.schemeKey);
  const aspect = (currentSize?.height || 3300) / (currentSize?.width || 2550);
  const thumbHeight = Math.round(thumbWidth * aspect);

  // Render background (scheme + line art + grain) at thumbnail resolution.
  // We request the raw canvas element back (returnCanvas: true) so we can
  // draw mock text directly on it — avoids a data-URL → Image roundtrip
  // that causes blank/black thumbnails due to async image decoding.
  const canvas = renderBackground({
    width: thumbWidth,
    height: thumbHeight,
    scheme,
    overlays: {
      lineArt: {
        type: variation.overlayType,
        density: variation.density,
        opacity: variation.opacity,
        seed: variation.seed,
      },
      grain: {
        enabled: true,
        density: variation.grainDensity,
        opacity: variation.grainOpacity,
      },
    },
    watermark: false,
    returnCanvas: true,
  });

  const ctx = canvas.getContext('2d');

  // Draw mock text elements to give a sense of the design
  drawMockText(ctx, thumbWidth, thumbHeight, scheme, variation);

  return canvas.toDataURL('image/jpeg', 0.85);
}

/**
 * Draw simplified text placeholders on the thumbnail to preview the layout feel.
 * Not actual Fabric.js objects — just rectangles and text draws for speed.
 */
function drawMockText(ctx, w, h, scheme, variation) {
  const fontPairing = variation.fontPairing;
  const titleFont = fontPairing?.title || 'Work Sans';
  const bodyFont = fontPairing?.body || 'DM Mono';
  const margin = w * 0.06;

  // Title area — large bold text block
  const titleSize = Math.round(h * 0.06);
  ctx.font = `bold ${titleSize}px "${titleFont}", sans-serif`;
  ctx.fillStyle = scheme.primary;
  ctx.textBaseline = 'top';
  ctx.fillText('EVENT NAME', margin, h * 0.12);

  // Date line
  const dateSize = Math.round(h * 0.025);
  ctx.font = `${dateSize}px "${bodyFont}", monospace`;
  ctx.fillStyle = scheme.accent;
  ctx.fillText('SAT APR 12 2026', margin, h * 0.22);

  // Venue
  const venueSize = Math.round(h * 0.03);
  ctx.font = `bold ${venueSize}px "${titleFont}", sans-serif`;
  ctx.fillStyle = scheme.secondary;
  ctx.fillText('THE VENUE', margin, h * 0.28);

  // Detail line
  ctx.font = `${dateSize}px "${bodyFont}", monospace`;
  ctx.fillStyle = scheme.detail;
  ctx.fillText('City, ST', margin, h * 0.34);

  // Lineup area — series of text blocks
  const lineupY = h * 0.45;
  ctx.font = `${Math.round(dateSize * 0.85)}px "${bodyFont}", monospace`;
  ctx.fillStyle = scheme.accent;
  ctx.fillText('FEATURING', margin, lineupY);

  const lineupSize = Math.round(h * 0.035);
  ctx.font = `bold ${lineupSize}px "${titleFont}", sans-serif`;
  ctx.fillStyle = scheme.primary;
  for (let i = 0; i < 3; i++) {
    ctx.fillText(`PERFORMER ${i + 1}`, margin, lineupY + dateSize * 1.5 + i * lineupSize * 1.3);
  }

  // Footer area
  const footerSize = Math.round(h * 0.015);
  ctx.font = `${footerSize}px "${bodyFont}", monospace`;
  ctx.fillStyle = scheme.footer;
  ctx.fillText('FOOTER TEXT', margin, h * 0.93);
  ctx.textAlign = 'right';
  ctx.fillText('STENCIL.CLOUD', w - margin, h * 0.93);
  ctx.textAlign = 'left';
}

// ─────────────────────────────────────────────────────────────────────────────
// VARIATION APPLICATION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a state patch from a variation config that can be applied to the editor.
 * The caller (app.js) is responsible for actually applying the patch to the canvas.
 *
 * @param {object} variation - variation config
 * @returns {object} state patch: { scheme, overlays, fontPairing }
 */
export function variationToStatePatch(variation) {
  return {
    schemeKey: variation.schemeKey,
    overlays: {
      lineArt: {
        type: variation.overlayType,
        density: variation.density,
        opacity: variation.opacity,
        seed: variation.seed,
      },
      grain: {
        enabled: true,
        density: variation.grainDensity,
        opacity: variation.grainOpacity,
      },
    },
    fontPairing: variation.fontPairing || null,
  };
}
